import {
    type ChildInsights,
    childInsightsSchema,
    type CreateGrowthInput,
    type CreateMedicationDoseInput,
    type CreateMedicationInput,
    type CreateMilestoneInput,
    type CreateReminderInput,
    exportPath,
    type FamilyExport,
    familyExportSchema,
    type GrowthMeasurement,
    growthMeasurementSchema,
    growthPath,
    growthMeasurementPath,
    insightsPath,
    type Medication,
    type MedicationDose,
    medicationDosePath,
    medicationDoseSchema,
    medicationDosesPath,
    medicationSchema,
    medicationsPath,
    type Milestone,
    milestonePath,
    milestoneSchema,
    milestonesPath,
    type Reminder,
    reminderPath,
    reminderSchema,
    remindersPath,
    type UpdateReminderInput,
} from '@baby-tracker/contracts';
import {
    useMutation,
    useQuery,
    useQueryClient,
    type UseMutationResult,
    type UseQueryResult,
} from '@tanstack/react-query';

import { apiGet, apiRequest } from '../lib/api-client.js';
import { queryKeys } from '../lib/query-keys.js';

function childQuery(childId: string | undefined): string {
    return childId === undefined ? '' : `?childId=${childId}`;
}

function useInvalidate(familyId: string, key: readonly unknown[]): () => Promise<void> {
    const queryClient = useQueryClient();
    return async () => {
        await queryClient.invalidateQueries({ queryKey: key });
        await queryClient.invalidateQueries({ queryKey: ['insights', familyId] });
    };
}

export function useInsights(
    familyId: string | undefined,
    childId: string | undefined,
    days: 7 | 30,
): UseQueryResult<ChildInsights> {
    return useQuery({
        queryKey: queryKeys.insights(familyId ?? '', childId ?? '', days),
        enabled: familyId !== undefined && childId !== undefined,
        queryFn: ({ signal }) =>
            apiGet(
                `${insightsPath(familyId ?? '')}?childId=${childId ?? ''}&days=${days}`,
                childInsightsSchema,
                { signal },
            ),
    });
}

export function useGrowth(
    familyId: string | undefined,
    childId: string | undefined,
): UseQueryResult<GrowthMeasurement[]> {
    return useQuery({
        queryKey: queryKeys.growth(familyId ?? '', childId ?? ''),
        enabled: familyId !== undefined && childId !== undefined,
        queryFn: ({ signal }) =>
            apiGet(
                `${growthPath(familyId ?? '')}${childQuery(childId)}`,
                growthMeasurementSchema.array(),
                { signal },
            ),
    });
}

export function useCreateGrowth(
    familyId: string,
): UseMutationResult<GrowthMeasurement, Error, CreateGrowthInput> {
    const invalidate = useInvalidate(familyId, ['growth', familyId]);
    return useMutation({
        mutationFn: (input: CreateGrowthInput) =>
            apiRequest('POST', growthPath(familyId), growthMeasurementSchema, { body: input }),
        onSuccess: invalidate,
    });
}

export function useDeleteGrowth(
    familyId: string,
): UseMutationResult<GrowthMeasurement, Error, string> {
    const invalidate = useInvalidate(familyId, ['growth', familyId]);
    return useMutation({
        mutationFn: (id: string) =>
            apiRequest('DELETE', growthMeasurementPath(id), growthMeasurementSchema),
        onSuccess: invalidate,
    });
}

export function useMilestones(
    familyId: string | undefined,
    childId: string | undefined,
): UseQueryResult<Milestone[]> {
    return useQuery({
        queryKey: queryKeys.milestones(familyId ?? '', childId ?? ''),
        enabled: familyId !== undefined && childId !== undefined,
        queryFn: ({ signal }) =>
            apiGet(
                `${milestonesPath(familyId ?? '')}${childQuery(childId)}`,
                milestoneSchema.array(),
                {
                    signal,
                },
            ),
    });
}

export function useCreateMilestone(
    familyId: string,
): UseMutationResult<Milestone, Error, CreateMilestoneInput> {
    const invalidate = useInvalidate(familyId, ['milestones', familyId]);
    return useMutation({
        mutationFn: (input: CreateMilestoneInput) =>
            apiRequest('POST', milestonesPath(familyId), milestoneSchema, { body: input }),
        onSuccess: invalidate,
    });
}

export function useDeleteMilestone(familyId: string): UseMutationResult<Milestone, Error, string> {
    const invalidate = useInvalidate(familyId, ['milestones', familyId]);
    return useMutation({
        mutationFn: (id: string) => apiRequest('DELETE', milestonePath(id), milestoneSchema),
        onSuccess: invalidate,
    });
}

export function useMedications(
    familyId: string | undefined,
    childId: string | undefined,
): UseQueryResult<Medication[]> {
    return useQuery({
        queryKey: queryKeys.medications(familyId ?? '', childId ?? ''),
        enabled: familyId !== undefined && childId !== undefined,
        queryFn: ({ signal }) =>
            apiGet(
                `${medicationsPath(familyId ?? '')}${childQuery(childId)}`,
                medicationSchema.array(),
                {
                    signal,
                },
            ),
    });
}

export function useCreateMedication(
    familyId: string,
): UseMutationResult<Medication, Error, CreateMedicationInput> {
    const invalidate = useInvalidate(familyId, ['medications', familyId]);
    return useMutation({
        mutationFn: (input: CreateMedicationInput) =>
            apiRequest('POST', medicationsPath(familyId), medicationSchema, { body: input }),
        onSuccess: invalidate,
    });
}

export function useDoses(
    familyId: string | undefined,
    childId: string | undefined,
): UseQueryResult<MedicationDose[]> {
    return useQuery({
        queryKey: queryKeys.doses(familyId ?? '', childId ?? ''),
        enabled: familyId !== undefined && childId !== undefined,
        queryFn: ({ signal }) =>
            apiGet(
                `${medicationDosesPath(familyId ?? '')}${childQuery(childId)}`,
                medicationDoseSchema.array(),
                { signal },
            ),
    });
}

export function useCreateDose(
    familyId: string,
): UseMutationResult<MedicationDose, Error, CreateMedicationDoseInput> {
    const invalidate = useInvalidate(familyId, ['doses', familyId]);
    return useMutation({
        mutationFn: (input: CreateMedicationDoseInput) =>
            apiRequest('POST', medicationDosesPath(familyId), medicationDoseSchema, {
                body: input,
            }),
        onSuccess: invalidate,
    });
}

export function useDeleteDose(familyId: string): UseMutationResult<MedicationDose, Error, string> {
    const invalidate = useInvalidate(familyId, ['doses', familyId]);
    return useMutation({
        mutationFn: (id: string) =>
            apiRequest('DELETE', medicationDosePath(id), medicationDoseSchema),
        onSuccess: invalidate,
    });
}

export function useReminders(
    familyId: string | undefined,
    childId: string | undefined,
): UseQueryResult<Reminder[]> {
    return useQuery({
        queryKey: queryKeys.reminders(familyId ?? '', childId ?? ''),
        enabled: familyId !== undefined,
        queryFn: ({ signal }) =>
            apiGet(
                `${remindersPath(familyId ?? '')}${childQuery(childId)}`,
                reminderSchema.array(),
                {
                    signal,
                },
            ),
    });
}

export function useCreateReminder(
    familyId: string,
): UseMutationResult<Reminder, Error, CreateReminderInput> {
    const invalidate = useInvalidate(familyId, ['reminders', familyId]);
    return useMutation({
        mutationFn: (input: CreateReminderInput) =>
            apiRequest('POST', remindersPath(familyId), reminderSchema, { body: input }),
        onSuccess: invalidate,
    });
}

export function useUpdateReminder(
    familyId: string,
): UseMutationResult<Reminder, Error, { id: string; body: UpdateReminderInput }> {
    const invalidate = useInvalidate(familyId, ['reminders', familyId]);
    return useMutation({
        mutationFn: (input: { id: string; body: UpdateReminderInput }) =>
            apiRequest('PATCH', reminderPath(input.id), reminderSchema, { body: input.body }),
        onSuccess: invalidate,
    });
}

export function useFamilyExport(familyId: string): UseMutationResult<FamilyExport, Error, void> {
    return useMutation({
        mutationFn: () => apiGet(exportPath(familyId), familyExportSchema),
    });
}

export type { FamilyExport };
